import { useState, useRef, useEffect, useCallback } from "react";
import { MediaRecorder, register } from "extendable-media-recorder";
import { connect } from "extendable-media-recorder-wav-encoder";
import { useTranslation } from "react-i18next";

// ---------------------------------------------------------------------------
// Module-level singleton — outside the hook to avoid React 18 StrictMode
// double-register. register() must only be called once per page lifetime.
// ---------------------------------------------------------------------------
let _registrationPromise: Promise<void> | null = null;
const ensureEncoder = (): Promise<void> => {
  if (!_registrationPromise) {
    _registrationPromise = connect().then((port) => register(port));
  }
  return _registrationPromise;
};

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------
export type RecorderState = "idle" | "recording" | "preview" | "uploading";

export interface UseVoiceNoteRecorderReturn {
  state: RecorderState;
  wavBlob: Blob | null;
  elapsed: number;
  micError: string | null;
  startRecording: () => Promise<void>;
  stopRecording: () => void;
  discard: () => void;
  reRecord: () => Promise<void>;
}

// ---------------------------------------------------------------------------
// Hook
// ---------------------------------------------------------------------------
export const useVoiceNoteRecorder = (): UseVoiceNoteRecorderReturn => {
  const { t } = useTranslation();

  // State
  const [state, setState] = useState<RecorderState>("idle");
  const [wavBlob, setWavBlob] = useState<Blob | null>(null);
  const [elapsed, setElapsed] = useState(0);
  const [micError, setMicError] = useState<string | null>(null);

  // Refs for mutable browser objects — never put in useState
  const streamRef = useRef<MediaStream | null>(null);
  const recorderRef = useRef<InstanceType<typeof MediaRecorder> | null>(null);
  const chunksRef = useRef<Blob[]>([]);
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const autoStopRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // ---------------------------------------------------------------------------
  // Stream teardown helper (VN-06)
  // ---------------------------------------------------------------------------
  const stopStream = useCallback(() => {
    streamRef.current?.getTracks().forEach((t) => t.stop());
    streamRef.current = null;
  }, []);

  const clearTimers = useCallback(() => {
    if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }
    if (autoStopRef.current) {
      clearTimeout(autoStopRef.current);
      autoStopRef.current = null;
    }
  }, []);

  // ---------------------------------------------------------------------------
  // stopRecording — clears timers and stops the recorder.
  // Stream tracks are NOT stopped here — they stay alive through the preview
  // state so callers can still access the audio. Tracks are released in
  // discard(), reRecord(), and the useEffect unmount cleanup (VN-06).
  // ---------------------------------------------------------------------------
  const stopRecording = useCallback(() => {
    clearTimers();
    if (recorderRef.current) {
      recorderRef.current.stop();
    }
  }, [clearTimers]);

  // ---------------------------------------------------------------------------
  // startRecording (VN-01)
  // ---------------------------------------------------------------------------
  const startRecording = useCallback(async () => {
    setMicError(null);
    try {
      await ensureEncoder();

      const stream = await navigator.mediaDevices.getUserMedia({
        audio: {
          sampleRate: { ideal: 16000 },
          channelCount: { exact: 1 },
          echoCancellation: true,
        },
      });

      streamRef.current = stream;
      chunksRef.current = [];

      const recorder = new MediaRecorder(stream, { mimeType: "audio/wav" });
      recorderRef.current = recorder;

      recorder.ondataavailable = (e) => {
        if (e.data.size > 0) {
          chunksRef.current.push(e.data);
        }
      };

      recorder.onstop = () => {
        const blob = new Blob(chunksRef.current, { type: "audio/wav" });
        setWavBlob(blob);
        setState("preview");
      };

      recorder.start(); // no timeslice — ondataavailable fires once on stop(), producing a single well-formed WAV
      setState("recording");

      // Elapsed timer — increments every second (VN-02)
      timerRef.current = setInterval(() => {
        setElapsed((s) => s + 1);
      }, 1000);

      // Auto-stop at 55s (VN-03)
      autoStopRef.current = setTimeout(() => {
        stopRecording();
      }, 55000);
    } catch (err) {
      // VN-05: mic denied or unavailable
      const msg =
        (err as DOMException).name === "NotAllowedError"
          ? t("healthMemory.voiceNote.micDeniedError")
          : t("healthMemory.voiceNote.micUnavailableError");
      setMicError(msg);
    }
  }, [stopRecording, t]);

  // ---------------------------------------------------------------------------
  // discard (VN-04, VN-06)
  // ---------------------------------------------------------------------------
  const discard = useCallback(() => {
    clearTimers();
    stopStream();
    setWavBlob(null);
    setElapsed(0);
    setState("idle");
  }, [clearTimers, stopStream]);

  // ---------------------------------------------------------------------------
  // reRecord (VN-04, VN-06)
  // ---------------------------------------------------------------------------
  const reRecord = useCallback(async () => {
    clearTimers();
    stopStream();
    setWavBlob(null);
    setElapsed(0);
    await startRecording();
  }, [clearTimers, stopStream, startRecording]);

  // ---------------------------------------------------------------------------
  // Cleanup on unmount (VN-06)
  // ---------------------------------------------------------------------------
  useEffect(() => {
    return () => {
      if (timerRef.current) clearInterval(timerRef.current);
      if (autoStopRef.current) clearTimeout(autoStopRef.current);
      stopStream();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return {
    state,
    wavBlob,
    elapsed,
    micError,
    startRecording,
    stopRecording,
    discard,
    reRecord,
  };
};

export default useVoiceNoteRecorder;
